
import { showToast } from './toast';
import { requestApi } from '../core/api-client';

export function getCurrentRole(): string {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    return (user.currentRole ?? '').toLowerCase();
  } catch {
    return '';
  }
}

export function getUserLogin(): any {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    return user;
  } catch {
    return null;
  }
}

// Hàm hỗ trợ tạo Slug chuẩn không dấu
export function generateSlug(str: string): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'd')
    .replace(/([^0-9a-z-\s])/g, '')
    .replace(/(\s+)/g, '-')
    .replace(/-+/g, '-');
}

export async function handleSubFormSubmit(
  formEl: HTMLFormElement,
  apiAction: () => Promise<void>,
  successMsg: string
): Promise<boolean> {
  const submitBtn = formEl.querySelector('button[type="submit"]') as HTMLButtonElement;
  let originalText = '';
  
  // 1. Disable nút và hiện hiệu ứng Loading
  if (submitBtn) {
    originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<i class="fas fa-spinner fa-spin" style="margin-right:6px;"></i>Đang xử lý...`;
  }

  try {
    // 2. Thực thi hàm gọi API được truyền vào
    await apiAction(); 
    
    // 3. Thông báo thành công
    showToast('success', successMsg);
    return true;
  } catch (err: any) {
    console.error('Lỗi khi submit sub-form:', err);
    // 4. Thông báo lỗi
    showToast('error', err.message || 'Có lỗi xảy ra khi lưu dữ liệu!');
    return false;
  } finally {
    // 5. Trả lại trạng thái cho nút
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }
}
export interface PagedSubDataOptions {
  endpoint: string;
  params?: Record<string, any>;
  skip: number;
  take: number;
  extraParams?: string;
  renderCallback: (items: any[]) => void;
  infoElementId: string;
  paginationElementId: string;
  onPageChange: (newSkip: number) => void;
  tableContainerId?: string; // Thêm tùy chọn container chứa bảng hoặc tự động dò
  isDevexpress?: boolean
}

/**
 * Hàm trợ giúp tải dữ liệu phân trang dùng chung cho các bảng con (Đã đồng bộ chuẩn chống giật và tham số)
 */
export const loadPagedSubData = async (options: PagedSubDataOptions) => {
  const {
    endpoint,
    params = {},
    skip,
    take,
    extraParams = '',
    renderCallback,
    infoElementId,
    paginationElementId,
    onPageChange,
    tableContainerId,
    isDevexpress = true
  } = options;

  // Tìm container bảng hoặc phần tử chứa bảng để cố định chiều cao chống giật layout
  const pagEl = document.getElementById(paginationElementId);
  let tableWrapper: HTMLElement | null = null;
  if (tableContainerId) {
    tableWrapper = document.getElementById(tableContainerId);
  } else if (pagEl) {
    // Tự động tìm thẻ chứa bảng hoặc tbody/table gần nhất phía trên phân trang
    tableWrapper = pagEl.closest('.table-responsive, .app-modal-body, div') as HTMLElement;
  }

  const currentHeight = tableWrapper ? tableWrapper.offsetHeight : 0;
  if (tableWrapper && currentHeight > 0) {
    tableWrapper.style.height = `${currentHeight}px`;
    tableWrapper.style.opacity = '0.5';
    tableWrapper.style.transition = 'opacity 0.15s ease-in-out';
  }

  try {
    // Xây dựng query string chuẩn khớp với BaseCatalogManager và DevExpress API
    const paramObj: Record<string, string> = {
      SkipCount: String(skip),
      MaxResultCount: String(take),
    };

    // Đưa thêm các params truyền vào (nếu có)
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        paramObj[key] = String(value);
      }
    });

    const queryParams = new URLSearchParams(paramObj);
    const query = `${queryParams.toString()}${extraParams}`;

    const url = isDevexpress == false ? `${endpoint}?${query}` 
    :( endpoint.includes('dev-express') ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}${query}` : `${endpoint}/dev-express?${query}`);
    const res = await requestApi<any>(url, 'GET');

    let items: any[] = [];
    let totalCount = 0;

    if (Array.isArray(res)) {
      items = res;
      totalCount = res.length;
    } else if (res?.data) {
      items = res.data;
      totalCount = res.totalCount ?? res.data.length;
    } else if (res?.items) {
      items = res.items;
      totalCount = res.totalCount ?? res.items.length;
    } else if (res?.result?.items) {
      items = res.result.items;
      totalCount = res.result.totalCount ?? res.result.items.length;
    }

    renderCallback(items);

    const actualTake = items.length > 0 ? take : take;

    renderSubPagination(
      totalCount,
      skip,
      actualTake,
      onPageChange,
      infoElementId,
      paginationElementId
    );
  } catch (err) {
    console.error(`Lỗi tải dữ liệu từ ${endpoint}:`, err);
  } finally {
    // Trả lại trạng thái bình thường cho khung bảng sau khi render xong
    if (tableWrapper) {
      tableWrapper.style.height = '';
      tableWrapper.style.opacity = '1';
    }
  }
};

/**
 * Hàm render giao diện nút phân trang nhỏ cho bảng con dùng chung (Giữ nguyên logic cũ + giữ vị trí cuộn)
 */
export const renderSubPagination = (
  totalCount: number,
  skip: number,
  take: number,
  onPageChange: (newSkip: number) => void,
  infoId: string,
  paginationId: string
) => {
  const infoEl = document.getElementById(infoId);
  const pagEl = document.getElementById(paginationId);
  if (!infoEl || !pagEl) return;

  if (totalCount === 0) {
    infoEl.textContent = 'Không có dữ liệu';
    pagEl.innerHTML = '';
    return;
  }

  const currentPage = Math.floor(skip / take) + 1;
  const totalPages = Math.ceil(totalCount / take);
  const startItem = skip + 1;
  const endItem = Math.min(skip + take, totalCount);

  infoEl.textContent = `Hiển thị ${startItem}-${endItem} trong tổng số ${totalCount}`;

  let html = '';
  // Nút Previous
  html += `<button type="button" class="btn btn-outline btn-sm" ${currentPage <= 1 ? 'disabled' : ''} data-skip="${Math.max(0, skip - take)}"><i class="fas fa-chevron-left"></i></button>`;

  // Thuật toán hiển thị tối đa 5 số trang + dấu ...
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  // Trang đầu tiên và dấu ... đầu
  if (startPage > 1) {
    html += `<button type="button" class="btn btn-outline btn-sm" data-skip="0">1</button>`;
    if (startPage > 2) {
      html += `<span class="btn btn-outline btn-sm" style="pointer-events: none; border: none;">...</span>`;
    }
  }

  // Các trang ở giữa
  for (let i = startPage; i <= endPage; i++) {
    const pageSkip = (i - 1) * take;
    const activeStyle = i === currentPage ? 'background: #11a75d; color: #fff; border-color: #11a75d;' : '';
    html += `<button type="button" class="btn btn-outline btn-sm" style="${activeStyle}" data-skip="${pageSkip}">${i}</button>`;
  }

  // Dấu ... cuối và trang cuối cùng
  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      html += `<span class="btn btn-outline btn-sm" style="pointer-events: none; border: none;">...</span>`;
    }
    const lastPageSkip = (totalPages - 1) * take;
    html += `<button type="button" class="btn btn-outline btn-sm" data-skip="${lastPageSkip}">${totalPages}</button>`;
  }

  // Nút Next
  html += `<button type="button" class="btn btn-outline btn-sm" ${currentPage >= totalPages ? 'disabled' : ''} data-skip="${skip + take}"><i class="fas fa-chevron-right"></i></button>`;

  pagEl.innerHTML = html;

  // Gắn sự kiện click phân trang kèm cơ chế lưu và khôi phục vị trí cuộn (scroll) của popup
  pagEl.querySelectorAll('button[data-skip]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (btn.hasAttribute('disabled')) return;

      // Tìm container cuộn bên trong popup (nếu popup có thanh cuộn)
      const modalCard = pagEl.closest('.app-modal-card, .modal-body, div');
      const scrollTop = modalCard ? (modalCard as HTMLElement).scrollTop : window.scrollY;
      const scrollLeft = modalCard ? (modalCard as HTMLElement).scrollLeft : window.scrollX;

      const targetSkip = parseInt(btn.getAttribute('data-skip') || '0', 10);
      
      onPageChange(targetSkip);

      // Khôi phục lại vị trí cuộn ngay sau khi đổi trang
      setTimeout(() => {
        if (modalCard) {
          (modalCard as HTMLElement).scrollTop = scrollTop;
          (modalCard as HTMLElement).scrollLeft = scrollLeft;
        } else {
          window.scrollTo({ top: scrollTop, left: scrollLeft, behavior: 'instant' });
        }
      }, 0);
    });
  });
};
/**
 * Hàm chung tự động tạo và hiển thị Popup Xác nhận trực quan
 * @param endpoint Đường dẫn API hoặc payload cần xử lý
 * @param title Tiêu đề hiển thị trên popup
 * @param message Nội dung câu hỏi xác nhận
 * @param method Phương thức HTTP (Mặc định là 'DELETE')
 * @param isQueryParams True nếu cần truyền tham số dạng Query String
 * @param deletePayload Dữ liệu thân hoặc params gửi đi nếu cần
 * @param onDeleteSuccess Callback thực thi khi xử lý thành công
 */
export function showDeletePopup(options: {
  endpoint: string;
  title?: string;
  message?: string;
  method?: 'DELETE' | 'POST' | 'PUT' | 'GET';
  isQueryParams?: boolean;
  deletePayload?: any;
  onDeleteSuccess?: () => void;
}) {
  const {
    endpoint,
    title = 'Xác nhận xóa',
    message = 'Bạn có chắc chắn muốn xóa bản ghi này không?',
    method = 'DELETE',
    isQueryParams = false,
    deletePayload,
    onDeleteSuccess,
  } = options;

  // 1. Xóa bỏ popup cũ nếu đang tồn tại trên DOM để tránh trùng lặp
  const existingModal = document.getElementById('dynamicDeletePopupOverlay');
  if (existingModal) {
    existingModal.remove();
  }

  // 2. Tạo cấu trúc HTML cho Popup chuẩn theo Design System
  const modalHtml = `
    <div class="app-modal-overlay active" id="dynamicDeletePopupOverlay" style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
      <div class="app-modal-card" style="background: #fff; width: 100%; max-width: 420px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); overflow: hidden;">
        <div class="app-modal-header" style="display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #ebedf2;">
          <h3 class="app-modal-title" style="margin: 0; font-size: 18px; font-weight: 600; color: #212529;">${title}</h3>
          <button type="button" class="app-modal-close" id="btnCloseDynamicModal" style="background: none; border: none; font-size: 16px; cursor: pointer; color: #6c757d;">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="app-modal-body" style="padding: 20px;">
          <p style="margin: 0; color: #495057; font-size: 14px; line-height: 1.5;">${message}</p>
        </div>

        <div class="app-modal-footer" style="display: flex; justify-content: flex-end; gap: 12px; padding: 12px 20px; background: #f8f9fa; border-top: 1px solid #ebedf2;">
          <button type="button" class="btn btn-outline" id="btnCancelDynamicDelete" style="padding: 8px 16px; border: 1px solid #ced4da; background: #fff; border-radius: 4px; cursor: pointer;">Hủy</button>
          <button type="button" class="btn btn-danger" id="btnConfirmDynamicDelete" style="padding: 8px 16px; background-color: #dc3545; color: #fff; border: none; border-radius: 4px; cursor: pointer;">
            <i class="fas fa-trash-alt"></i> Xác nhận
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modalOverlay = document.getElementById('dynamicDeletePopupOverlay');
  const btnClose = document.getElementById('btnCloseDynamicModal');
  const btnCancel = document.getElementById('btnCancelDynamicDelete');
  const btnConfirm = document.getElementById('btnConfirmDynamicDelete') as HTMLButtonElement;

  // Hàm đóng và đồng thời tự động gỡ bỏ hoàn toàn HTML ra khỏi DOM
  const closeModal = () => {
    if (modalOverlay) {
      modalOverlay.classList.remove('active');
      modalOverlay.remove();
    }
  };

  btnClose?.addEventListener('click', closeModal);
  btnCancel?.addEventListener('click', closeModal);
  modalOverlay?.addEventListener('click', (e) => {
    if (e.target === modalOverlay) closeModal();
  });

  // 3. Xử lý sự kiện bấm Xác nhận có hiệu ứng Loading và gọi API theo method được chỉ định
  btnConfirm?.addEventListener('click', async () => {
    const originalHtml = btnConfirm.innerHTML;
    btnConfirm.disabled = true;
    btnConfirm.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Đang xử lý...';

    try {
      let url = endpoint;
      if (deletePayload && isQueryParams) {
        const queryParams = new URLSearchParams(deletePayload);
        url = `${endpoint}?${queryParams.toString()}`;
      }

      await requestApi(url, method, isQueryParams ? undefined : deletePayload);

      //showToast('success', 'Thành công!');
      closeModal();

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (err) {
      console.error(`Lỗi khi thực hiện ${method}:`, err);
      showToast('error', 'Có lỗi xảy ra!');
    } finally {
      if (btnConfirm) {
        btnConfirm.disabled = false;
        btnConfirm.innerHTML = originalHtml;
      }
    }
  });
}

export const processUserRolesAndMenus = (
    user: any,
    selectedRoleName?: string
): any => {

    // ============================================================
    // 1. Chuẩn hóa Roles
    // ============================================================

    const roles = Array.isArray(user?.roles)
        ? user.roles
        : [];


    // ============================================================
    // 2. Hàm lọc và xử lý Menu
    // ============================================================

    const filterMenus = (menus: any[]): any[] => {

        if (!Array.isArray(menus)) {
            return [];
        }

        // ========================================================
        // Bước 1:
        // Lấy các menu chức năng thực tế
        // ========================================================

        const validIds = new Set<string>();

        menus.forEach(item => {

            if (
                item &&
                item.path &&
                item.path !== '#'
            ) {
                validIds.add(item.id);
            }

        });


        // ========================================================
        // Bước 2:
        // Đưa các menu cha vào danh sách
        // ========================================================

        let changed = true;

        while (changed) {

            changed = false;

            menus.forEach(item => {

                if (
                    item &&
                    item.idCha &&
                    validIds.has(item.id) &&
                    !validIds.has(item.idCha)
                ) {

                    validIds.add(item.idCha);

                    changed = true;
                }

            });

        }


        // ========================================================
        // Bước 3:
        // Lọc menu hợp lệ
        // ========================================================

        const result = menus
            .filter(item =>
                item &&
                validIds.has(item.id)
            )
            .map(item => ({
                ...item
            }));


        // ========================================================
        // Bước 4:
        // Cha chỉ có đúng 1 con trực tiếp
        //
        // Ví dụ:
        //
        // customer_mgr
        //     └── customer_list
        //
        // Kết quả:
        //
        // customer_list
        //     title = "Quản lý khách hàng"
        //     path  = "/customer/customer-list"
        //
        // Chỉ xử lý Cha -> Con.
        // Không xử lý Ông -> Cha.
        // ========================================================

        const removeItems = new Set<any>();


        // Lấy ID của các menu đang có con trực tiếp
        const parentIds = new Set<string>(
            result
                .filter(item => item.idCha)
                .map(item => item.idCha)
        );


        parentIds.forEach(parentId => {

            // ----------------------------------------------------
            // Tìm menu cha
            // ----------------------------------------------------

            const parent = result.find(
                item => item.id === parentId
            );

            if (!parent) {
                return;
            }


            // ----------------------------------------------------
            // Chỉ lấy các con trực tiếp
            // ----------------------------------------------------

            const children = result.filter(
                item => item.idCha === parentId
            );

        });


        // ========================================================
        // Bước 5:
        // Xóa các menu con đã merge vào cha
        // ========================================================

        return result.filter(
            item => !removeItems.has(item)
        );
    };


    // ============================================================
    // 3. Gán Roles vào User
    // ============================================================

    user.roles = roles;


    // ============================================================
    // 4. Reset Role và Menu hiện tại
    // ============================================================

    user.currentRole = null;
    user.menus = [];


    // ============================================================
    // 5. Xác định Role được chọn
    //
    // Nếu truyền selectedRoleName:
    //     dùng role đó
    //
    // Nếu không truyền:
    //     lấy role đầu tiên
    // ============================================================

    const roleName =
        selectedRoleName?.trim() ||
        roles[0]?.name;


    // ============================================================
    // 6. Nếu không có Role
    // ============================================================

    if (!roleName) {
        return user;
    }


    // ============================================================
    // 7. Tìm Role
    // ============================================================

    const selectedRole = roles.find(
        x =>
            x?.name?.toLowerCase() ===
            roleName.toLowerCase()
    );


    // ============================================================
    // 8. Nếu không tìm thấy Role
    // ============================================================

    if (!selectedRole) {
        return user;
    }


    // ============================================================
    // 9. Gán Role hiện tại
    // ============================================================

    user.currentRole = selectedRole.name;


    // ============================================================
    // 10. Lấy Menu của Role hiện tại
    // ============================================================

    user.menus = filterMenus(
        Array.isArray(selectedRole.menus)
            ? selectedRole.menus
            : []
    );


    // ============================================================
    // 11. Return User
    // ============================================================

    return user;
};